import { LocationOn, Hotel, DirectionsWalk } from "@mui/icons-material";

export const blogData = {
  "nginx-plus": {
    id: 1,
    head: "Cutting NGINX Plus Costs with Intelligent EC2 Optimization",
    subHead:
      "How to Save on Both Infrastructure and Licensing Costs Without Sacrificing Performance",
    date: " May 12, 2025",
    time: " 5 min read",
    views: " 1.7k views",
    image: "/images/blog-nginx.png",
    author: "By Awsmo Team",
    initials: "AT",
    occupation: "",
    content: [
      "Introduction",
      "Dual Cost Problem with NGINX Plus",
      "We Come In",
      "Savings Potential",
    ],
    para: [
      {
        type: "head",
        value: "Introduction",
        spacingAfter: 2,
      },
      {
        type: "text",
        value:
          "NGINX Plus is one of the most popular high-performance load balancers, reverse proxies, and API gateways used by modern enterprises. With its advanced features—such as active health checks, dynamic reconfiguration, and application-aware load balancing—NGINX Plus has become a go-to choice for organizations running mission-critical workloads.",
      },
      {
        type: "text",
        value:
          "However, there’s a catch: NGINX Plus now offers usage-based pricing. This means that in addition to the cost of running EC2 instances, you’re also paying for every hour that your NGINX Plus software is running—even when traffic is minimal.",
      },
      {
        type: "text",
        value:
          "If your environment experiences variable traffic patterns—such as spikes during business hours but long stretches of low usage—you could be paying far more than necessary.",
      },
      {
        type: "text",
        value: "That’s where our optimization platform comes in.",
        spacingAfter: 5,
      },
      {
        type: "head",
        value: "The Dual Cost Problem with NGINX Plus",
        spacingAfter: 2,
      },
      {
        type: "text",
        value: "Let’s break it down.",
      },
      {
        type: "text",
        value:
          "When you deploy NGINX Plus on AWS EC2 behind an Application Load Balancer (ALB), you’re paying for:",
      },
      {
        type: "bullet",
        value:
          "EC2 Compute Cost – The hourly cost of the instances running NGINX Plus.",
      },
      {
        type: "bullet",
        value:
          "NGINX Plus Licensing Cost – Charged based on active usage (e.g., per hour of running time).",
      },
      {
        type: "text",
        value:
          "During off-peak hours, when your application is receiving minimal traffic, those EC2 instances—and the NGINX Plus licenses running on them—are sitting idle but still racking up charges.",
        spacingAfter: 5,
      },
      {
        type: "head",
        value: "Where We Come In",
        spacingAfter: 2,
      },
      {
        type: "text",
        value:
          "Our SaaS platform is designed to monitor, predict, and optimize your EC2 usage behind load balancers—without sacrificing performance or reliability.",
      },
      {
        type: "text",
        value: "When applied to NGINX Plus deployments, here’s how we help:",
        spacingAfter: 5,
      },
      {
        type: "head",
        value: "The Savings Potential",
        spacingAfter: 2,
      },
      {
        type: "text",
        value:
          "Let’s imagine you’re running 5 m5.large EC2 instances with NGINX Plus, each costing:",
      },
      { type: "bullet", value: "$0.096/hour (EC2 On-Demand)" },
      {
        type: "bullet",
        value: "$0.05/hour (NGINX Plus license example pricing)",
        spacingAfter: 2,
      },
      {
        type: "text",
        value: "That’s $0.146/hour per instance, or ~$525/month for all 5.",
        spacingAfter: 5,
      },
      {
        type: "text",
        value:
          "If traffic drops by 60% during nights and weekends, scaling down could save you:",
      },
      { type: "bullet", value: "EC2 Cost Savings: ~$315/month" },
      { type: "bullet", value: "NGINX Plus License Savings: ~$164/month" },
      {
        type: "bullet",
        value: "Total Savings: ~$479/month — nearly $5,750/year.",
        spacingAfter: 3,
      },
      {
        type: "text",
        value:
          "And that’s without even using Spot instances, which could increase savings further.",
        spacingAfter: 5,
      },
      {
        type: "text",
        value:
          "If you’re running NGINX Plus on AWS EC2, you’re probably paying twice for idle time—once for the instance, and once for the software license. Our intelligent EC2 optimization platform solves that problem by scaling based on real-time metrics and predictive traffic patterns.",
        spacingAfter: 3,
      },
      {
        type: "text",
        value: "The result?",
      },
      { type: "bullet", value: "Lower EC2 bills" },
      { type: "bullet", value: "Lower NGINX Plus license bills" },
      { type: "bullet", value: "No performance compromise" },
    ],
  },
  "ami-plus": {
    id: 0,
    head: "Save Big on Specialized AMIs, Not Just EC2 Costs",
    subHead:
      "How to Save on Both Infrastructure and Licensing Costs Without Sacrificing Performance",
    date: " June 15, 2025",
    time: " 6 min read",
    views: " 2.0k views",
    image: "/images/blog-ami.png",
    author: "Aseem Gupta",
    initials: "AG",
    occupation:
      "CTO and Product Manager of Awsmo, lead the company’s technology vision, ensuring scalable and secure cloud solutions , while also driving product strategy, roadmap, and innovation to deliver maximum value to customers.",
    content: [
      "Introduction",
      "The Hidden Drain: High-Cost AMIs",
      "Why Traditional Cost Optimization Misses the Mark",
      "Smarter Savings: How Our Tool Fixes the Gap",
      "Conclusion: Stop Paying for Idle Time",
    ],
    para: [
      {
        type: "head",
        value: "Introduction",
        spacingAfter: 2,
      },
      {
        type: "text",
        value:
          "When most teams think about AWS cost optimization, their minds go straight to EC2 compute costs — rightsizing, Reserved Instances, or Spot pricing. While these are useful, they overlook one of the biggest hidden expenses in the cloud: specialized AWS Marketplace AMIs.",
        spacingAfter: 3,
      },
      {
        type: "text",
        value:
          "These AMIs bundle powerful third-party software (firewalls, load balancers, analytics platforms, etc.) into ready-to-use EC2 instances. But they come with two price tags:",
      },
      {
        type: "bullet",
        value:
          "EC2 compute cost – charged by AWS for the underlying virtual machine.",
      },
      {
        type: "bullet",
        value:
          "Software license cost – charged by the vendor, billed per hour as long as the instance is running.",
        spacingAfter: 4,
      },
      {
        type: "text",
        value:
          "In many cases, the license fee is 50–100x higher than the compute cost. For example, a $10/hr license on a $0.10/hr EC2 instance still totals $10.10/hr — and the license runs 24/7, whether or not you’re using it.",
      },

      {
        type: "head",
        value: "The Hidden Drain: High-Cost AMIs",
        spacingAfter: 2,
      },
      {
        type: "text",
        value:
          "Specialized AMIs such as NGINX Plus, Fortinet, and Palo Alto firewalls deliver critical functionality, but their pricing structure punishes inefficiency. The challenge is that license charges accrue continuously, even when an instance is idle.",
        spacingAfter: 5,
      },
      {
        type: "text",
        value: "Consider a common scenario:",
      },
      {
        type: "bullet",
        value:
          "A premium AMI priced at $10/hour for the license plus $0.10/hour for the EC2 instance.",
      },
      {
        type: "bullet",
        value:
          "Running 5 such instances 24/7 means a monthly bill of over $3,600.",
      },
      {
        type: "bullet",
        value:
          "The vast majority of this cost—almost the entire bill—is the software license fee.",
        spacingAfter: 5,
      },
      {
        type: "text",
        value:
          "What this means is every idle hour doesn’t just waste pennies on compute, it wastes dollars on licenses. Multiply this across a production environment, and the hidden cost quickly balloons.",
        spacingAfter: 5,
      },

      {
        type: "head",
        value: "Why Traditional Cost Optimization Misses the Mark",
        spacingAfter: 2,
      },
      {
        type: "text",
        value:
          "Traditional cost-optimization tactics miss this pitfall. Rightsizing instances, purchasing EC2 Reserved Instances (RIs) or Savings Plans, and turning off non-essential servers all help lower compute bills—but do nothing to eliminate the license charges on Marketplace AMIs. In fact, AWS itself makes clear that annual software subscriptions don’t apply to AWS infrastructure charges. You can combine a software subscription with an EC2 RI to trim the compute line item, but the third-party license cost remains unchanged. Similarly, switching to Spot Instances only reduces EC2 costs, not the paid-license component of the AMI.",
      },
      {
        type: "text",
        value:
          "In practice, this means the largest chunk of spend for a license-based AMI is completely untouched by conventional savings strategies. And manually shutting down or scaling in these instances is rarely realistic—most are deployed behind load balancers or serve critical backend traffic. Stopping them without automation risks downtime and user impact.",
        spacingAfter: 2,
      },
      {
        type: "text",
        value:
          "In short, the usual focus on compute efficiency leaves a giant cost blind spot when it comes to specialized AMIs.",
        spacingAfter: 2,
      },

      {
        type: "head",
        value: "Smarter Savings: How Our Tool Fixes the Gap",
        spacingAfter: 2,
      },
      {
        type: "text",
        value:
          "This is where our optimization tool changes the equation. Instead of only looking at EC2, it targets the much larger AMI licensing costs head-on. With a single click, you activate an automation layer that:",
      },

      {
        type: "bullet",
        value: "Scales in real time based on traffic, CPU, or memory usage.)",
      },
      {
        type: "bullet",
        value:
          "Integrates seamlessly with load balancers, ensuring smooth registration and deregistration of instances.",
      },
      {
        type: "bullet",
        value:
          "Eliminates idle-time waste by automatically stopping expensive AMIs when demand is low.",
      },
      {
        type: "bullet",
        value:
          "Predicts demand spikes, scaling up in advance to avoid performance issues.",
      },
      {
        type: "bullet",
        value:
          "Optionally, runs Spot Instances for non-critical workloads, cutting EC2 costs further on top of license savings.",
        spacingAfter: 4,
      },
      {
        type: "text",
        value:
          "The result is a system that maximizes availability during peak load but aggressively trims costs when traffic slows.",
        spacingAfter: 5,
      },
      {
        type: "head",
        value: "Conclusion: Stop Paying for Idle Time",
        spacingAfter: 2,
      },
      {
        type: "text",
        value:
          "Specialized AMIs deliver critical functionality, but their license costs can quietly consume a huge portion of your cloud budget. Traditional cost optimization strategies focus on the wrong side of the bill, leaving this expense untouched.",
      },
      {
        type: "text",
        value:
          "Our solution changes that. With one click, it automatically scales your AMIs to match real demand, cutting both license and compute costs — all while maintaining uptime and performance.",
      },
      {
        type: "text",
        value:
          "Why keep paying for capacity you’re not using? Connect your AWS account today, enable optimization, and start saving thousands — without downtime, without complexity, and without compromising performance.",
      },
    ],
  },
  "awsmo-setup": {
    id: 2,
    head: "Quick Awsmo Account Setup, Get Started in Minutes",
    subHead:
      "One-click onboarding, secure ARN verification, transparent and instant visibility into cost and performance metrics.",
    date: " July 20, 2025",
    time: " 6 min read",
    views: " 2.4k views",
    image: "/images/blog-account.png",
    author: "Chetan Jain",
    initials: "CJ",
    occupation:
      "As the CTO and Product Manager of Awsmo, I lead the company’s technology vision, ensuring scalable and secure cloud solutions , while also driving product strategy, roadmap, and innovation to deliver maximum value to customers.",
    content: [
      "Introduction",
      "Sign Up",
      "Connect Your AWS Account",
      "Discover Your Services",
      "Go Live ",
    ],
    para: [
      {
        type: "head",
        value: "Introduction",
        spacingAfter: 2,
      },
      {
        type: "text",
        value:
          "Setting up your AWSmo account is fast, simple, and designed to get you optimizing your AWS infrastructure without any heavy lifting. Whether you’re running a few EC2 instances or managing a fleet behind load balancers, AWSmo makes it easy to start saving costs while keeping performance rock solid.",
        spacingAfter: 3,
      },
      {
        type: "text",
        value:
          "Here’s a quick guide to help you set up your AWSmo account in just a few steps:",
        spacingAfter: 2,
      },
      {
        type: "head",
        value: "1. Sign Up",
        spacingAfter: 4,
      },
      {
        type: "text",
        value:
          "Head over to awsmo.console and create your account using your email address.",
        spacingAfter: 3,
      },
      {
        type: "bullet",
        value: "Enter your email address and create a secure password.",
      },
      {
        type: "bullet",
        value: "Verify your email to activate your account.",
      },
      {
        type: "bullet",
        value: "Log in to your AWSmo account and unlock all setup options.",
        spacingAfter: 3,
      },
      {
        type: "head",
        value: "2. Connect Your AWS Account",
        spacingAfter: 4,
      },

      {
        type: "text",
        value:
          "To optimize your infrastructure, AWSmo needs secure access to your AWS environment. Don’t worry—we only request least-privilege IAM permissions required to:",
        spacingAfter: 3,
      },
      {
        type: "bullet",
        value: "Read CloudWatch metrics (CPU, memory, load balancer stats)",
      },
      {
        type: "bullet",
        value:
          "Adjust Auto Scaling Group (ASG) capacity behind your load balancers",
        spacingAfter: 3,
      },

      {
        type: "text",
        value: "Steps:",
        spacingAfter: 3,
      },

      {
        type: "text",
        value:
          "1. Open AWS CloudShell in your AWS console and run the command provided to download the setup script.",
      },
      {
        type: "text",
        value:
          "2. Once the script is downloaded, run it by typing: python add_arn.py.",
      },
      {
        type: "text",
        value: "3. Go back to the AWSmo console and enter your Client ARN.",
      },
      {
        type: "text",
        value: "4. AWSmo will securely verify the ARN in just a few seconds.",
      },
      {
        type: "text",
        value:
          "5. After verification, your AWS account is successfully connected — and you’re all set to log in to the AWSmo dashboard.",
      },
      {
        type: "text",
        value: "That’s it! ✅",
        spacingAfter: 3,
      },

      {
        type: "head",
        value: "3. Discover Your Services",
        spacingAfter: 4,
      },

      {
        type: "text",
        value: "Once connected, AWSmo will automatically detect:",
        spacingAfter: 3,
      },
      {
        type: "bullet",
        value: "Your EC2 Auto Scaling Groups",
      },
      {
        type: "bullet",
        value: "The load balancers (ALB/NLB/CLB) behind them",
        spacingAfter: 3,
      },

      {
        type: "text",
        value:
          "You’ll see a clear list of services you can choose to optimize.",
        spacingAfter: 3,
      },

      {
        type: "head",
        value: "4. Go Live 🚀",
        spacingAfter: 4,
      },

      {
        type: "text",
        value: "When you’re ready, flip the switch → Enable Optimization.",
        spacingAfter: 3,
      },
      {
        type: "bullet",
        value:
          "AWSmo starts monitoring CPU, memory, and LB metrics in real times",
      },
      {
        type: "bullet",
        value:
          "Predictive scaling kicks in for traffic surges and release events",
      },
      {
        type: "bullet",
        value: "Safe scale-ins reduce costs once the traffic wave passes",
        spacingAfter: 3,
      },

      {
        type: "text",
        value: "🎉 Congrats you’ve set up AWSmo in minutes!",
        spacingAfter: 3,
      },
      {
        type: "text",
        value: "From here, you can track:",
        spacingAfter: 3,
      },

      {
        type: "bullet",
        value: "Savings vs baseline (how much you’re cutting from AWS bills)",
      },
      {
        type: "bullet",
        value: "Performance KPIs (latency, 5xx rate, connection counts)",
      },
      {
        type: "bullet",
        value: "Event history (every scale action, with full audit log)",
        spacingAfter: 3,
      },

      {
        type: "text",
        value:
          "Most autoscaling and cost-optimization tools are powerful but painfully complex. They expect your teams to invest days—or even weeks—in setup, tuning, and ongoing babysitting. AWSmo flips that model by offering plug-and-play optimization, so you can start saving costs within minutes.",
        spacingAfter: 3,
      },

      {
        type: "text",
        value:
          "With AWSmo, you can instantly cut down EC2 costs without making any changes to your applications. The platform continuously optimizes your instances behind AWS load balancers, ensuring you only pay for the capacity you actually need. When traffic spikes during live sports, premieres, campaigns, or breaking news, AWSmo predicts the surge and pre-scales resources so performance never suffers. As demand drops, it safely scales back to reduce waste. And with clear dashboards, you can track both the cost savings and performance stability in real time, giving your team full confidence in every decision.",
        spacingAfter: 2,
      },
    ],
  },
  "spot-on-demand": {
    id: 3,
    head: "Spot vs On-Demand vs Savings Plans: A Complete Guide to AWS Pricing Models",
    subHead:
      "Cut Through the Confusion of AWS Pricing Models and Learn How to Save More While Keeping Your Cloud Flexible and Scalable",
    date: " August 08, 2025",
    time: " 4 min read",
    views: " 1.4k views",
    image: "/images/blog-instanceType.png",
    author: "Chetan Jain",
    initials: "CJ",
    occupation:
      "As the CTO and Product Manager of Awsmo, I lead the company’s technology vision, ensuring scalable and secure cloud solutions , while also driving product strategy, roadmap, and innovation to deliver maximum value to customers.",
    content: [
      "Introduction",
      "On-Demand Instances",
      "Spot Instances",
      "Savings Plans",
      "Savings Plans Are a Trap — Here’s the Way Out ",
    ],
    para: [
      {
        type: "head",
        value: "Introduction",
        spacingAfter: 2,
      },
      {
        type: "text",
        value:
          "One of the biggest advantages of cloud computing is its pay-as-you-go nature. Instead of making large upfront investments in hardware, businesses can consume resources on demand and pay only for what they use. Amazon Web Services (AWS) has taken this concept even further by offering different pricing models for its compute services, each designed to balance flexibility, cost, and commitment. The three primary models — On-Demand Instances, Spot Instances, and Savings Plans — serve very different needs, and understanding them is key to optimizing both performance and cloud spend.",
        spacingAfter: 3,
      },
      {
        type: "head",
        value: "On-Demand Instances",
        spacingAfter: 2,
      },
      {
        type: "text",
        value:
          "On-Demand Instances are the most straightforward and flexible option available on AWS. With this model, you pay for compute capacity by the second (with a one-minute minimum) and there are no contracts or commitments. This freedom makes On-Demand a natural choice for teams experimenting in the cloud, for developers who need temporary environments, and for workloads that scale up suddenly without predictable patterns.",
        spacingAfter: 3,
      },
      {
        type: "text",
        value: "<b> Key Benefits </b> ",
        spacingAfter: 2,
      },
      {
        type: "bullet",
        value: "High Flexibility: Start or stop instances anytime.",
      },
      {
        type: "bullet",
        value: "No Commitments: Perfect for unpredictable workloads.",
      },
      {
        type: "bullet",
        value:
          "Easy to Scale: Great for sudden traffic spikes or experimenting.",
        spacingAfter: 3,
      },
      {
        type: "text",
        value: "<b> Limitations </b>",
        spacingAfter: 2,
      },
      {
        type: "bullet",
        value:
          "Highest Cost per Hour: On-Demand pricing is the most expensive option.",
      },
      {
        type: "bullet",
        value:
          "Not Cost-Effective at Scale: If your workload runs 24/7, you’ll end up paying more compared to other models.",
        spacingAfter: 3,
      },
      {
        type: "head",
        value: "Spot Instances",
      },

      {
        type: "text",
        value:
          "If your workloads are flexible, Spot Instances offer the deepest discounts in AWS, often up to 90% compared to On-Demand pricing. The reason is simple: Spot Instances allow customers to purchase unused EC2 capacity at heavily reduced rates. This makes them highly attractive for organizations that run large-scale data processing, analytics pipelines, or machine learning training tasks, where costs can otherwise spiral quickly.",
        spacingAfter: 3,
      },
      {
        type: "text",
        value: "<b> Key Benefits </b> ",
        spacingAfter: 2,
      },
      {
        type: "bullet",
        value: "Huge Savings: The most cost-effective option available.",
      },
      {
        type: "bullet",
        value:
          "Great for Scaling: Ideal for workloads that need lots of compute power temporarily.",
      },
      {
        type: "bullet",
        value:
          "Supports Auto Scaling: Can work well with containers and Kubernetes clusters.",
        spacingAfter: 3,
      },
      {
        type: "text",
        value: "<b> Limitations </b>",
        spacingAfter: 2,
      },
      {
        type: "bullet",
        value:
          "No Guaranteed Availability: You may not always get the capacity you request.",
      },
      {
        type: "bullet",
        value:
          "Interruptions Possible: Workloads must be able to tolerate interruptions.",
      },

      {
        type: "bullet",
        value:
          "Operational Complexity: Requires extra planning for checkpointing, retries, or fault tolerance.",
        spacingAfter: 4,
      },
      {
        type: "text",
        value: "<b> Example Use Cases </b> ",
        spacingAfter: 2,
      },
      {
        type: "bullet",
        value:
          "Batch Processing: Big data analytics, media rendering, machine learning training..",
      },
      {
        type: "bullet",
        value:
          "CI/CD Pipelines: Build and test environments that can restart easily.",
      },

      {
        type: "bullet",
        value:
          "Background Jobs: Anything that doesn’t require continuous uptime.",
        spacingAfter: 4,
      },

      {
        type: "head",
        value: "Savings Plans",
        spacingAfter: 2,
      },

      {
        type: "text",
        value:
          "Savings Plans let you commit to a fixed amount of compute usage for one or three years in exchange for discounts of up to 72%. They’re designed for predictable, steady workloads like production servers or databases.",
        spacingAfter: 3,
      },
      {
        type: "text",
        value:
          "However, the downside is significant. Once you commit, you pay whether you use it or not. If your workloads shrink, shift to containers or serverless, or your business needs change, you’re stuck paying for unused capacity. In practice, this locks you into AWS and removes the flexibility that makes the cloud valuable. Instead of guaranteed savings, Savings Plans can easily turn into wasted spend and long-term financial handcuffs.",
        spacingAfter: 2,
      },

      {
        type: "head",
        value: "Savings Plans Are a Trap — Here’s the Way Out",
        spacingAfter: 2,
      },
      {
        type: "text",
        value:
          "With AWSMO, you don’t need to gamble on the future. Instead of committing to one- or three-year contracts, AWSMO continuously analyzes your cloud usage and automatically optimizes costs in real time. You get the better savings without the risk of overcommitment.",
      },

      {
        type: "bullet",
        value:
          "<b> Predictable Costs Without Guesswork </b>  – No need to forecast usage years in advance.",
      },
      {
        type: "bullet",
        value:
          "<b> Freedom to Innovate </b> – Scale in, Scale out, or switch architectures without being tied to rigid contracts.",
      },

      {
        type: "bullet",
        value:
          "<b> Higher Savings Potential </b> – Optimize across On-Demand, Spot, and reserved capacity seamlessly.",
      },
      {
        type: "bullet",
        value:
          "<b> Automatic  Optimization </b> – Real-time monitoring ensures you always pay the lowest possible rate.",
      },
    ],
  },
};

export const blogCards = [
  {
    id: 0,
    title: "Save Big on Specialized AMIs, Not Just EC2 Costs",
    desc: "Save on Both Infrastructure and Licensing Cost",
    icon: <Hotel fontSize="small" />,
    color: "#E8F0FE",
    href: "/blog/ami-plus",
    img: "/images/blog-ami.png",
    date: " June 15, 2025",
  },
  {
    id: 1,
    title: "Cutting NGINX Plus Costs with EC2 Optimization",
    desc: "Save on Both Infrastructure and Licensing Cost",
    icon: <LocationOn fontSize="small" />,
    color: "#E6F4EA",
    href: "/blog/nginx-plus",
    img: "/images/blog-nginx.png",
    date: " May 12, 2025",
  },
  {
    id: 2,
    title: "Getting Started with AWSmo in Minutes",
    desc: "A guided setup that gets you predictive scaling and savings fast.",
    icon: <DirectionsWalk fontSize="small" />,
    color: "#F3E8FF",
    href: "/blog/awsmo-setup",
    img: "/images/blog-account.png",
    date: " July 20, 2025",
  },
  {
    id: 3,
    title: "Spot vs On-Demand vs Savings Plans:",
    desc: "A Complete Guide to AWS Pricing Models",
    icon: <DirectionsWalk fontSize="small" />,
    color: "#F3E8FF",
    href: "/blog/spot-on-demand",
    img: "/images/blog-instanceType.png",
    date: " August 08, 2025",
  },
];
